import { useTranslation } from 'react-i18next';

import { formatDashboardDay } from '../dashboardDay';
import { dashboardLocale } from '../dashboardLocale';
import { FeedingKpiCard } from '../FeedingKpiCard/FeedingKpiCard';
import type { KpiLine } from '../FeedingKpiCard/KpiLineText';
import { formatTarget } from '../PenComparisonTable/penComparisonFormat';
import { systemLabels } from '../systemLabel';

import {
    cardQuality,
    formatNumber,
    type KpiCardProps,
    kpiWarningMarker,
    NO_BREAK,
} from './kpiCardFormat';
import { coverageNote } from './kpiCoverageNote';
import { footerTarget, type KpiTarget, kpiWarning, pensIn, resolveKpiTarget } from './kpiTargets';

/** The calendar day before `day` ('YYYY-MM-DD' in, 'YYYY-MM-DD' out). */
function previousDay(day: string): string {
    const parsed = Date.parse(`${day}T00:00:00Z`);
    if (Number.isNaN(parsed)) return day;
    return new Date(parsed - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

export function FcrKpiCard(props: KpiCardProps) {
    const { summary, config, pens, closingDay } = props;
    const { t, i18n } = useTranslation();
    const locale = dashboardLocale(i18n);
    // IPS 40: name the customer's own system rather than a generic "PMS".
    const system = systemLabels(t, config.pmsSystemName);
    // Each value is judged against the targets of the pens it averages, and the Target row
    // names the targets of the pens behind either value (IPS 10, 11).
    const targetOf = (ids: number[]): KpiTarget =>
        resolveKpiTarget(pensIn(pens, ids), config, 'fcr');
    const target = footerTarget(
        pens,
        [summary.coverage.fcrPms, summary.coverage.fcrBioscope],
        config,
        'fcr'
    );
    // A bFCR above the target is the warning, so the target says so, as RGI's does.
    const targetText =
        target.kind === 'shared'
            ? `≤${NO_BREAK}${formatTarget(target.target.target, 'fcr', locale)}`
            : t('feedingModule.kpi.targetRange', {
                  min: formatTarget(target.min, 'fcr', locale),
                  max: formatTarget(target.max, 'fcr', locale),
              });

    // The stored bFCR for a day covers the 7 days ending the day BEFORE it, a known
    // off-by-one in the service that is left alone because correcting it would move every
    // figure customers have signed off. Every other closing-day figure is on the closing
    // day, so spelling out this card's own window is the only place a reader can see the
    // difference. Without it the number silently claims a day it does not cover.
    const fcrWindowEnd = closingDay === null ? null : previousDay(closingDay);
    const fcrWindow =
        fcrWindowEnd === null
            ? t('feedingModule.kpi.fcrRolling')
            : t('feedingModule.kpi.fcrRollingEnding', { day: formatDashboardDay(fcrWindowEnd) });
    // The ± line carries what it is and the window in its tooltip, so the card stays short
    // (U11). Over several pens the ± is the interval of their mean. Without a ± the window
    // goes into a tooltip on the Bioscope figure instead (U14), so the card has no line a
    // card with a ± lacks. A dash covers no window, so it opens nothing.
    const bioscopeInterval: KpiLine | null =
        summary.fcrBioscopeConfInterval == null
            ? null
            : {
                  text: `±${formatNumber(summary.fcrBioscopeConfInterval, 3, locale)}`,
                  explanation: [
                      t(
                          summary.coverage.fcrBioscope.length > 1
                              ? 'feedingModule.kpi.fcrIntervalMeanTip'
                              : 'feedingModule.kpi.fcrIntervalTip'
                      ),
                      fcrWindow,
                  ],
              };
    const bioscopeExplanation =
        bioscopeInterval === null && Number.isFinite(summary.fcrBioscope) ? [fcrWindow] : undefined;
    const { note, noteReasons } = coverageNote(
        [
            { metric: 'fcrPms', label: system.name, namesSystem: true },
            { metric: 'fcrBioscope', label: t('feedingModule.bioscope') },
        ],
        props,
        t
    );

    return (
        <FeedingKpiCard
            label={t('feedingModule.kpi.fcrBio')}
            quality={cardQuality(summary, summary.fcrBioscope != null)}
            values={[
                {
                    label: system.name,
                    text: formatNumber(summary.fcrPms, 2, locale),
                    warning: kpiWarningMarker(
                        kpiWarning('fcrSystem', summary.fcrPms, targetOf(summary.coverage.fcrPms)),
                        system,
                        t,
                        locale
                    ),
                },
                {
                    label: t('feedingModule.bioscope'),
                    text: formatNumber(summary.fcrBioscope, 2, locale),
                    explanation: bioscopeExplanation,
                    warning: kpiWarningMarker(
                        kpiWarning(
                            'fcrBioscope',
                            summary.fcrBioscope,
                            targetOf(summary.coverage.fcrBioscope)
                        ),
                        system,
                        t,
                        locale
                    ),
                },
            ]}
            details={[
                { label: t('feedingModule.kpi.target'), value: targetText },
                // Its own row, so the ± and its tooltip visibly belong to the Bioscope
                // value and not to the planning system's.
                ...(bioscopeInterval === null
                    ? []
                    : [{ label: t('feedingModule.bioscope'), value: bioscopeInterval }]),
            ]}
            note={note}
            noteReasons={noteReasons}
        />
    );
}
